export function formatMoney(
  cents: number,
  maximumFractionDigits = cents % 100 === 0 ? 0 : 2,
): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits,
  }).format(cents / 100);
}

export function displayHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

interface AttributionOptions {
  source: string;
  medium: string;
  campaign: string;
  content?: string;
}

/**
 * Adds standard campaign attribution without mutating the canonical URL kept
 * in the paid ledger. Existing UTM values are replaced so every Outbid click
 * has one unambiguous source in the destination site's analytics.
 */
export function addAttribution(url: string, options: AttributionOptions): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("utm_source", options.source);
    parsed.searchParams.set("utm_medium", options.medium);
    parsed.searchParams.set("utm_campaign", options.campaign);
    if (options.content) parsed.searchParams.set("utm_content", options.content);
    return parsed.toString();
  } catch {
    return url;
  }
}

function shortDisplayHash(value: string): string {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0).toString(36).padStart(6, "0").slice(-6);
}

export function displayUrlIdentity(url: string, maximumLength = 40): string {
  const normalized = normalizeUrl(url);
  try {
    const parsed = new URL(normalized);
    const host = parsed.host;
    const path = parsed.pathname === "/" ? "" : parsed.pathname;
    const display = `${host}${path}${parsed.search}`;
    if (display.length <= maximumLength) return display;

    const suffix = `…${shortDisplayHash(normalized)}`;
    return `${display.slice(0, Math.max(1, maximumLength - suffix.length))}${suffix}`;
  } catch {
    return normalized.slice(0, maximumLength) || "Website";
  }
}

const TRACKING_QUERY_PARAMETERS = new Set([
  "_ga",
  "_gl",
  "dclid",
  "fbclid",
  "gbraid",
  "gclid",
  "li_fat_id",
  "mc_cid",
  "mc_eid",
  "msclkid",
  "ttclid",
  "twclid",
  "wbraid",
]);

export function normalizeUrl(url: string): string {
  try {
    const withProtocol = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const parsed = new URL(withProtocol);
    parsed.hostname = parsed.hostname.toLowerCase();
    parsed.hash = "";

    for (const key of [...parsed.searchParams.keys()]) {
      const normalizedKey = key.toLowerCase();
      if (normalizedKey.startsWith("utm_") || TRACKING_QUERY_PARAMETERS.has(normalizedKey)) {
        parsed.searchParams.delete(key);
      }
    }
    parsed.searchParams.sort();

    return parsed.toString().replace(/\/$/, "");
  } catch {
    return url.trim().toLowerCase();
  }
}
